import { PopperPlacementType } from "@mui/base/Popper";
import { ExistsFilterOperator } from "@redotech/redo-views-common/advanced-filters/exists-filter";
import { memo, useEffect, useMemo, useState } from "react";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
} from "../../arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "../../arbiter-components/list/redo-list";
import { RedoListDropdown } from "../../arbiter-components/list/redo-list-dropdown";
import { SimpleFilterDropdown } from "../../filters/simple-filter-dropdown";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { ExistsTableFilter } from "../advanced-filter";

function existsBooleanOptions(
  boolDisplay: ExistsTableFilter["boolDisplay"],
): RedoListItem<boolean>[] {
  if (boolDisplay) {
    return [
      { id: "true", type: "text", value: true, text: boolDisplay.trueDisplay },
      {
        id: "false",
        type: "text",
        value: false,
        text: boolDisplay.falseDisplay,
      },
    ];
  }
  return [
    { id: "true", type: "text", value: true, text: "true" },
    { id: "false", type: "text", value: false, text: "false" },
  ];
}

/**
 * The value-selection dropdown for an exists/boolean filter, rendered without
 * the surrounding pill chip. Used both by `ExistsFilterGroup` (anchored to the
 * chip) and by the add-filter menu (anchored to the menu button) so the value
 * can be chosen before a pill is committed.
 */
export const ExistsValueDropdown = memo(function ExistsValueDropdown({
  filter,
  setFilter,
  anchor,
  open,
  setOpen,
  placement,
}: {
  filter: ExistsTableFilter;
  setFilter(filter: ExistsTableFilter): void;
  anchor: HTMLElement | null;
  open: boolean;
  setOpen(open: boolean): void;
  placement?: PopperPlacementType;
}) {
  const { value } = filter.data;
  const booleanOptions = useMemo(
    () => existsBooleanOptions(filter.boolDisplay),
    [filter.boolDisplay],
  );

  return (
    <RedoListDropdown
      dropdownAnchor={anchor}
      dropdownOpen={open}
      dropdownPlacement={placement}
      items={booleanOptions}
      itemSelected={({ item }) => {
        setFilter({ ...filter, data: { ...filter.data, value: item.value } });
      }}
      selectedItems={booleanOptions.find((o) => o.value === value)?.id}
      setDropdownOpen={setOpen}
      size="xs"
    />
  );
});

export const ExistsFilterGroup = memo(function BooleanFilterGroup({
  filter,
  setFilter,
  removeFilter,
  openOnRender,
}: {
  filter: ExistsTableFilter;
  setFilter(filter: ExistsTableFilter): void;
  removeFilter(): void;
  openOnRender: boolean;
}) {
  const [valueRef, setValueRef] = useState<HTMLButtonElement | null>(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { value, operator } = filter.data;
  const boolDisplay = filter.boolDisplay;

  function getDisplayString(value: boolean | undefined): string {
    if (!boolDisplay) {
      return value ? "true" : "false";
    }
    if (value === true) {
      return boolDisplay.trueDisplay;
    } else if (value === false) {
      return boolDisplay.falseDisplay;
    }
    return "...";
  }

  useEffect(() => {
    if (openOnRender && valueRef) {
      valueRef.click();
    }
  }, [openOnRender, valueRef]);

  const valueText = value == null ? "..." : getDisplayString(value);

  const valueDropdownAnchor = (
    <RedoFilterDropdownAnchor
      color="primary"
      onClick={() => setDropdownOpen(!dropdownOpen)}
      ref={setValueRef}
      text={valueText}
      tooltip={
        value != null ? (
          <Flex dir="column">
            <Text>{valueText}</Text>
          </Flex>
        ) : undefined
      }
      weight="medium"
    />
  );

  return (
    <>
      <ExistsValueDropdown
        anchor={valueRef}
        filter={filter}
        open={dropdownOpen}
        setFilter={setFilter}
        setOpen={setDropdownOpen}
      />
      <RedoFilterGroup
        Icon={filter.Icon}
        propertyName={filter?.label ? filter.label : filter.displayName}
        query={
          <SimpleFilterDropdown
            filterStyle="query"
            options={filter.operators || Object.values(ExistsFilterOperator)}
            optionToFriendlyName={(value) => existsTypeToText[value]}
            setValue={(value) => {
              setFilter({
                ...filter,
                data: { ...filter.data, operator: value },
              });
            }}
            value={operator}
          />
        }
        removeFilter={removeFilter}
        value={valueDropdownAnchor}
      />
    </>
  );
});

const existsTypeToText: Record<ExistsFilterOperator, string> = {
  [ExistsFilterOperator.EXISTS]: "is",
  [ExistsFilterOperator.NOT_EXISTS]: "is not",
};
